/**
 * 为异步页面组件补充与路由一致的组件名，
 * 以便 keep-alive 的 include 可以稳定命中。
 * @param {Object} component 组件配置对象
 * @param {string} routeName 路由名称
 * @returns {Object}
 */
function withRouteComponentName(component, routeName) {
  if (!routeName || !component || typeof component !== 'object') {
    return component;
  }

  return {
    ...component,
    name: routeName,
  };
}

/**
 * 包装路由异步组件加载器，并在首次解析后缓存解析结果，
 * 避免每次导航都返回新的组件对象，影响过渡和缓存复用。
 * @param {Function} loader 异步组件加载函数
 * @param {string} routeName 路由名称
 * @returns {Function}
 */
function wrapAsyncComponent(loader, routeName) {
  let resolvedComponent;

  return () => {
    if (resolvedComponent) {
      return resolvedComponent;
    }

    return Promise.resolve(loader()).then(mod => {
      const component = mod.default || mod;
      resolvedComponent = withRouteComponentName(component, routeName);
      return resolvedComponent;
    });
  };
}

/**
 * 递归包装路由对象，为命名路由的异步页面组件自动注入组件名。
 * 这样后续新增页面时，只需要维护路由 name 即可。
 * @param {Object} route 路由配置对象
 * @returns {Object}
 */
export default function createRoute(route) {
  if (!route || typeof route !== 'object') {
    return route;
  }

  const normalizedRoute = {
    ...route,
  };

  if (typeof normalizedRoute.component === 'function' && normalizedRoute.name) {
    normalizedRoute.component = wrapAsyncComponent(normalizedRoute.component, normalizedRoute.name);
  }

  if (Array.isArray(normalizedRoute.children)) {
    normalizedRoute.children = normalizedRoute.children.map(child => createRoute(child));
  }

  return normalizedRoute;
}
